import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Mail, MessageSquare, CreditCard, Settings, RefreshCw, Trash2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212]">
              LabrHood<span className="text-[#0055FF]">Labs</span>
            </span>
          </a>
          <Button 
            asChild
            variant="ghost"
            className="text-sm font-medium"
            data-testid="back-home-btn"
          >
            <a href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-white border-t border-gray-100" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <span className="font-heading text-lg font-bold tracking-tight text-[#121212] mb-4">
            LabrHood<span className="text-[#0055FF]">Labs</span>
          </span>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function YesChefSupport() {
  const faqs = [
    {
      question: "How does the chef chat work?",
      answer: "Yes Chef uses AI to answer your cooking questions in real-time. Just type or speak your question — like \"What can I make with chicken and broccoli?\" or \"How long do I bake salmon at 400°F?\" — and the chef assistant will provide recipes, cooking tips, and step-by-step guidance. The AI learns from your pantry and shopping list to give personalized suggestions.",
      icon: MessageSquare
    },
    {
      question: "What do the subscription tiers include?",
      answer: "Yes Chef offers two subscription tiers:\n\n• Base — Access to the chef chat assistant, smart shopping lists, and pantry management. Limited daily AI interactions.\n\n• Yes Chef Pro — Unlimited chef chat interactions, premium voice responses, advanced recipe suggestions, priority support, and exclusive features.\n\nBoth tiers are billed monthly and auto-renew until canceled.",
      icon: CreditCard
    },
    {
      question: "How do I manage or cancel my subscription?",
      answer: "On iPhone: Go to Settings → tap your name at the top → Subscriptions → select Yes Chef → Manage or Cancel.\n\nYou can also manage your subscription directly in the App Store app under your account settings.\n\nCancellation takes effect at the end of your current billing period. You'll continue to have access until then.",
      icon: Settings
    },
    {
      question: "How do I restore my purchases?",
      answer: "If your subscription isn't showing up after reinstalling the app or switching devices, go to the in-app Settings and tap \"Restore Purchases.\" This will sync your subscription status with your Apple ID. Make sure you're signed in with the same Apple ID used for the original purchase.",
      icon: RefreshCw
    },
    {
      question: "How do I delete my account?",
      answer: "To delete your Yes Chef account and all associated data:\n\n1. Open the Yes Chef app\n2. Go to Settings (gear icon)\n3. Scroll down and tap \"Delete Account\"\n4. Confirm the deletion\n\nThis will permanently remove your account, shopping lists, pantry data, and chat history. This action cannot be undone. If you have an active subscription, remember to cancel it separately through your iPhone Settings to avoid future charges.",
      icon: Trash2
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Hero */}
          <AnimatedSection className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
              Support
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#121212] mb-6" data-testid="support-headline">
              Yes Chef Support
            </h1>
            <p className="text-lg text-gray-600 font-light leading-relaxed">
              Yes Chef is your AI kitchen helper — a smart chef chat assistant with an intelligent shopping list that helps you cook with confidence.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Contact Section */}
          <AnimatedSection className="mb-16">
            <div className="bg-[#F9FAFB] p-8 sm:p-12">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
                    Contact Us
                  </h2>
                  <p className="text-gray-600 leading-relaxed font-light mb-4">
                    Have a question, issue, or feedback? We're here to help.
                  </p>
                  <a 
                    href="mailto:sravenscroft80@gmail.com" 
                    className="text-[#0055FF] hover:underline font-medium"
                    data-testid="contact-email"
                  >
                    sravenscroft80@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* FAQ Section */}
          <AnimatedSection className="mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#121212] mb-8">
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-gray-200 px-6 data-[state=open]:bg-[#F9FAFB]"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex items-center gap-4 text-left">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#F3F4F6] flex items-center justify-center">
                        <faq.icon className="w-5 h-5 text-[#0055FF]" />
                      </div>
                      <span className="font-heading text-lg font-semibold text-[#121212]">
                        {faq.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pl-14">
                    <p className="text-gray-600 leading-relaxed font-light whitespace-pre-line">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Legal Links */}
          <AnimatedSection>
            <h3 className="font-heading text-xl font-bold tracking-tight text-[#121212] mb-6">
              Legal
            </h3>
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="privacy-link"
              >
                <a href="/yes-chef/privacy-policy">Privacy Policy</a>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="terms-link"
              >
                <a href="/yes-chef/terms-of-use">Terms of Use</a>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </main>

      <Footer />
    </div>
  );
}
